import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, MoreHorizontal } from 'lucide-react';
import { Button } from './Button';
import { useEscapeToClose } from './useEscapeToClose';

/** Roughly the tallest a menu gets, used to decide whether to flip upwards. */
const MENU_MAX_HEIGHT = 280;

/** Gap between the trigger and the floating panel. */
const MENU_OFFSET = 8;

/**
 * @typedef {object} DropdownItem
 * @property {string} label
 * @property {() => void} [onClick]
 * @property {React.ComponentType} [icon]
 * @property {boolean} [disabled]
 * @property {boolean} [danger] - Painted red, for irreversible actions.
 * @property {boolean} [dividerBefore] - Rule above the item, to separate a
 *   destructive action from the routine ones above it.
 * @property {string} [description] - Second line under the label. Use it to say
 *   why an item is disabled: a greyed-out row with no reason reads as broken.
 */

/**
 * Menu of actions hanging off a compact trigger.
 *
 * The panel is mounted on `document.body` through a portal and positioned from
 * the trigger's box. Rendering it in place would let any ancestor with
 * `overflow` clip it — the order header, for one, hides its overflow to keep a
 * blurred decoration inside its rounded corners — and no `z-index` can undo a
 * clip. It flips above the trigger when there is not enough room below.
 *
 * The trigger follows the platform's icon rule: with a label it shows the icon
 * describing the action on the left and a chevron on the right, because the
 * right slot is what announces that something opens. Without a label it is a
 * bare icon affordance and the chevron would only add noise.
 *
 * @param {object} props
 * @param {React.ComponentType} [props.triggerIcon] - Left of the label. Falls
 *   back to a "more" glyph when there is no label.
 * @param {string} [props.triggerLabel]
 * @param {Array<DropdownItem>} [props.items]
 */
export const DropdownMenu = ({
  triggerIcon: TriggerIcon,
  triggerLabel,
  items = []
}) => {
  const triggerRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState(null);

  const close = () => setIsOpen(false);

  const open = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;

    const spaceBelow = window.innerHeight - rect.bottom;
    const flipUp = spaceBelow < MENU_MAX_HEIGHT && rect.top > spaceBelow;

    setPosition({
      // Right-aligned with the trigger, which is how the menu reads next to a
      // stack of buttons pinned to the right of a header.
      right: window.innerWidth - rect.right,
      top: flipUp ? undefined : rect.bottom + MENU_OFFSET,
      bottom: flipUp ? window.innerHeight - rect.top + MENU_OFFSET : undefined,
    });
    setIsOpen(true);
  };

  // Escape closes and hands the focus back to the trigger. The overlay below
  // covers the mouse, and only the mouse: without this there is no way out of
  // the menu with a keyboard, and the focus is left on a button that is gone.
  // Through the shared stack, so a menu inside a dialog closes alone.
  useEscapeToClose(isOpen, () => {
    close();
    triggerRef.current?.focus();
  });

  const handleItemClick = (onClick, e) => {
    e.stopPropagation();
    if (onClick) onClick();
    close();
  };

  const menu = isOpen && position && createPortal(
    <>
      {/* Full-screen catcher, per the project's outside-click rule. */}
      <div className="fixed inset-0 z-[10000]" onClick={close} onWheel={close} />
      <div
        style={{
          position: 'fixed',
          top: position.top,
          bottom: position.bottom,
          right: position.right,
        }}
        data-testid="dropdown-menu"
        className="z-[10001] min-w-[16rem] w-max bg-white/90 dark:bg-[#1a1a1a]/90 backdrop-blur-md rounded-2xl shadow-2xl border border-border overflow-hidden animate-in fade-in zoom-in-95 duration-200"
      >
        <div role="menu" className="p-1">
          {items.map((item, idx) => {
            const Icon = item.icon;
            return (
              <React.Fragment key={idx}>
                {item.dividerBefore && <div className="my-1 border-t border-border" />}
                <button
                  type="button"
                  role="menuitem"
                  onClick={(e) => handleItemClick(item.onClick, e)}
                  disabled={item.disabled}
                  data-testid="dropdown-item"
                  data-item={item.label}
                  className={`w-full flex items-start text-left gap-3 px-3 py-2.5 text-sm font-medium rounded-xl transition-colors
                    ${item.disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-ninja-gray dark:hover:bg-[#2d2d2d]'}
                    ${item.danger ? 'text-red-500 hover:text-red-600' : 'text-ninja-dark dark:text-white'}
                  `}
                >
                  {Icon && <Icon size={16} className="mt-0.5 shrink-0" />}
                  <span>
                    {item.label}
                    {item.description && (
                      <span className="block text-xs font-normal text-text-muted mt-0.5">
                        {item.description}
                      </span>
                    )}
                  </span>
                </button>
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </>,
    document.body
  );

  return (
    <div className="relative inline-block text-left">
      <Button
        ref={triggerRef}
        variant="ghost"
        icon={triggerLabel ? TriggerIcon : TriggerIcon || MoreHorizontal}
        iconRight={triggerLabel ? ChevronDown : undefined}
        iconOnly={!triggerLabel}
        aria-label={triggerLabel ? undefined : 'Más acciones'}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        data-testid="dropdown-trigger"
        onClick={(e) => {
          e.stopPropagation();
          if (isOpen) close(); else open();
        }}
      >
        {triggerLabel}
      </Button>

      {menu}
    </div>
  );
};
